import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { extractVin } from '../lib/vin';

// Full-screen camera overlay that scans a VIN barcode (Code 39 / Data Matrix /
// QR / PDF417 / Code 128) and returns the 17-char VIN via onResult.
export default function VinScanner({ onResult, onClose }) {
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const onResultRef = useRef(onResult);
  const [error, setError] = useState('');
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);

  useEffect(() => { onResultRef.current = onResult; }, [onResult]);

  // Torch (flashlight) toggle. Only exposed on cameras that support it —
  // mainly Android Chrome and newer iOS; the button hides itself otherwise.
  function toggleTorch() {
    const track = trackRef.current;
    if (!track) return;
    const next = !torchOn;
    track.applyConstraints({ advanced: [{ torch: next }] })
      .then(() => setTorchOn(next))
      .catch(() => setTorchAvailable(false));
  }

  useEffect(() => {
    // Camera access requires a secure context (https:// or localhost). On a
    // plain http:// LAN address (e.g. testing over WiFi from a phone) the
    // browser hides getUserMedia entirely, which used to surface as a vague
    // "Could not access the camera." — this catches it with a clear message.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera access needs a secure connection. Open the live ShopTrack site (https://) rather than a local network address.');
      return;
    }

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_128,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.QR_CODE,
      BarcodeFormat.PDF_417,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);

    const reader = new BrowserMultiFormatReader(hints);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let stopped = false;
    let stream = null;
    let intervalId = null;

    navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        // VIN barcodes are dense 1D bars — request a high-res feed and
        // continuous autofocus so a door-jamb sticker held close stays sharp.
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        advanced: [{ focusMode: 'continuous' }],
      },
    }).then(s => {
      if (stopped) { s.getTracks().forEach(t => t.stop()); return; }
      stream = s;
      const video = videoRef.current;
      if (video) { video.srcObject = s; video.play().catch(() => {}); }

      const track = s.getVideoTracks()[0];
      if (track) {
        trackRef.current = track;
        const caps = (track.getCapabilities && track.getCapabilities()) || {};
        if (caps.torch) setTorchAvailable(true);
        if (caps.focusMode && caps.focusMode.includes && caps.focusMode.includes('continuous')) {
          track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
        }
      }

      // Decode loop: instead of handing ZXing the whole camera frame (where a
      // door-jamb barcode is a small strip that blurs when downscaled), crop a
      // centered wide band — the area inside the on-screen targeting box — so
      // the barcode fills the decoded image at full detail. This is the single
      // biggest reliability win for 1D VIN barcodes on phones.
      intervalId = window.setInterval(() => {
        if (stopped || !video || !video.videoWidth) return;
        const vw = video.videoWidth, vh = video.videoHeight;
        // ~2.6:1 band (VIN-barcode aspect), 92% of frame width, centered.
        let cw = Math.round(vw * 0.92);
        let ch = Math.round(cw / 2.6);
        if (ch > vh * 0.9) { ch = Math.round(vh * 0.9); cw = Math.min(vw, Math.round(ch * 2.6)); }
        const sx = Math.round((vw - cw) / 2);
        const sy = Math.round((vh - ch) / 2);
        canvas.width = cw;
        canvas.height = ch;
        ctx.drawImage(video, sx, sy, cw, ch, 0, 0, cw, ch);
        try {
          const result = reader.decodeFromCanvas(canvas);
          const vin = result && extractVin(result.getText());
          if (vin) {
            stopped = true;
            window.clearInterval(intervalId);
            onResultRef.current(vin);
          }
        } catch (e) {
          // No barcode in this frame — ZXing throws NotFoundException; expected.
        }
      }, 220);
    }).catch(e => {
      setError(e?.name === 'NotAllowedError'
        ? 'Camera permission denied. Allow camera access and try again.'
        : e?.name === 'NotFoundError'
        ? 'No camera found on this device.'
        : (e?.message || 'Could not access the camera.'));
    });

    return () => {
      stopped = true;
      if (intervalId) window.clearInterval(intervalId);
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, []);

  return (
    <div className="modal-overlay" style={{ zIndex: 400, background: '#000', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ height: 'calc(64px + env(safe-area-inset-top))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', flexShrink: 0, paddingTop: 'env(safe-area-inset-top)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#fff' }}>Scan VIN</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {torchAvailable && (
            <button onClick={toggleTorch} aria-label="Toggle flashlight" style={{ background: torchOn ? 'var(--orange)' : 'rgba(255,255,255,0.14)', border: 'none', color: torchOn ? '#16100b' : '#fff', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2h6v3l-1 3v2h-4V8L9 5z"/><path d="M10 10h4v10a2 2 0 0 1-4 0z"/><line x1="12" y1="13" x2="12" y2="15"/></svg>
            </button>
          )}
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        </div>
      </div>

      {/* Camera */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
        {/* Targeting frame */}
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: '84%', maxWidth: 380, height: 130, border: '2px solid var(--orange)', borderRadius: 10, boxShadow: '0 0 0 2000px rgba(0,0,0,0.45)' }} />
        {error && (
          <div style={{ position: 'absolute', bottom: 24, left: 16, right: 16, background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.45)', color: '#fff', borderRadius: 10, padding: '12px 14px', fontSize: 13, textAlign: 'center' }}>{error}</div>
        )}
      </div>

      {/* Hint */}
      <div style={{ flexShrink: 0, padding: '14px 16px calc(16px + env(safe-area-inset-bottom))', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
        Point the camera at the VIN barcode — door jamb sticker or windshield label
      </div>
    </div>
  );
}
